import React, {useEffect, useState} from 'react';
import {Modal, Button, List, AutoComplete, Row, Col, Input, message, Checkbox} from 'antd';
import {TestControllerApi, TestDto, TestPlanControllerApi, TestPlanDto, TestPlanTestDto} from '../../../api';
import {DeleteOutlined, PlusOutlined} from "@ant-design/icons";
import {useKeycloakHeader} from "../../../config/Config";

/*
Dieses Modal ist zur Erstellung eines Testplans zuständig
 */
const TestPlanCreationModal = ({onTestPlanCreated}) => {
    const [tests, setTests] = useState<TestDto[]>([]);
    const [selectedTests, setSelectedTests] = useState<TestPlanTestDto[]>([]);
    const [name, setName] = useState<string>('');
    const [testName, setTestName] = useState<string>('');
    const [visible, setVisible] = useState<boolean>(false);
    const [searchOptions, setSearchOptions] = useState<{ value: string }[]>([]);
    const [filteredOptions, setFilteredOptions] = useState<{ value: string }[]>([]);
    const [nameError, setNameError] = useState(false);

    const headers = useKeycloakHeader();
    const testControllerApi = new TestControllerApi();
    const testPlanControllerApi = new TestPlanControllerApi();

    useEffect(() => {
        fetchTests();
    }, []);

    const fetchTests = () => {
        testControllerApi.getAllTestsMinimal(headers).then((axiosResponse) => {
            const testDtos: TestDto[] = axiosResponse.data;
            setTests(testDtos);
            const options = testDtos.map((test) => ({value: test.name || ''}));
            setSearchOptions(options);
            setFilteredOptions(options);
        }).catch((error) => {
            console.error(error);
        });
    };


    const handleAddTest = (value: string) => {
        const selectedTest = tests.find(test => test.name === value);
        let selectedTestPlanTestDto: TestPlanTestDto = {testDto: selectedTest, activeTrace: false};
        if (selectedTest) {
            setSelectedTests([...selectedTests, selectedTestPlanTestDto]);
        }
        setName('');
        setFilteredOptions(searchOptions);
    };

    const handleNameChange = (value: string) => {
        setName(value);
        const filteredOptions = searchOptions.filter(option =>
            option.value.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredOptions(filteredOptions);
    };

    const handleTestNameChange = (value: string) => {
        setTestName(value);
        if (value) {
            setNameError(false);
        }
    };


    const handleSubmit = () => {
        if (!testName) {
            setNameError(true);
            return;
        }

        let testPlanDto: TestPlanDto = {
            name: testName,
            testPlanTestDtos: selectedTests
        } as TestPlanDto;
        console.log("Ergebnis:", testPlanDto);
        testPlanControllerApi.createTestPlan(testPlanDto, headers).then(response => {
            // Erfolgreiche Antwort
            message.success('Testplan wurde erzeugt');
            onTestPlanCreated(true);
            setVisible(false);
        }).catch((error) => {
            message.error({
                content: "Testplan konnte nicht erstellt werden!",
                duration: 15
            });
        })
    };

    const handleRemoveTest = (test: TestPlanTestDto) => {
        setSelectedTests(selectedTests.filter(item => item.testDto?.id !== test.testDto?.id));
    };

    const handleCheckboxChange = (test: TestPlanTestDto, checked: boolean) => {
        const updatedTests = selectedTests.map(t =>
            t.testDto?.id === test.testDto?.id ? {...t, activeTrace: checked} : t
        );
        setSelectedTests(updatedTests); // Aktualisiere den State
    };


    return (
        <>
            <Button type="primary" onClick={() => setVisible(true)}>
                <PlusOutlined/>
            </Button>
            <Modal
                title="Testplan erstellen"
                open={visible}
                onCancel={() => setVisible(false)}
                footer={
                    <div style={{textAlign: 'center', width: '100%'}}>
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 16}}>
                            <Button
                                key="submit"
                                type="primary"
                                onClick={handleSubmit}
                                style={{width: '20%', marginLeft: '5%'}}
                            >
                                Speichern
                            </Button>
                        </div>
                    </div>
                }
            >
                <div style={{paddingBottom: '20px', borderBottom: '1px solid #f0f0f0', marginBottom: '20px'}}>
                    <div style={{marginBottom: 8}}>Name des Testplans</div>
                    <Input
                        id="testplan-name"
                        placeholder="Name des Testplans"
                        value={testName}
                        onChange={(e) => handleTestNameChange(e.target.value)}
                        style={{width: '100%'}}
                    />
                    {nameError && (
                        <div style={{color: 'red', marginTop: 8}}>
                            Name ist erforderlich.
                        </div>
                    )}
                </div>

                <h3>Tests:</h3>
                <AutoComplete
                    options={filteredOptions}
                    placeholder="Test hinzufügen"
                    onSelect={handleAddTest}
                    style={{marginBottom: 16, width: '100%'}}
                    onChange={handleNameChange}
                    value={name}
                />

                <div style={{marginTop: 16}}>
                    <h3>Ausgewählte Tests:</h3>
                    <List
                        bordered
                        locale={{emptyText: 'Keine Tests ausgewählt'}}
                        dataSource={selectedTests}
                        style={{maxHeight: '300px', overflowY: 'auto'}}
                        renderItem={(test: TestPlanTestDto, index) => (
                            <List.Item
                                key={test.testDto ? test.testDto.id : test.id}
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('index', index.toString());
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const dragIndex = parseInt(e.dataTransfer.getData('index'));
                                    const newTests = [...selectedTests];
                                    const temp = newTests[dragIndex];
                                    newTests[dragIndex] = newTests[index];
                                    newTests[index] = temp;
                                    setSelectedTests(newTests);
                                }}
                                style={{cursor: 'move'}}
                            >
                                <Row style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%'
                                }}>
                                    <Col>{test.testDto?.name}</Col>
                                    <Col>
                                        <Checkbox
                                            checked={test.activeTrace}
                                            onChange={(e) => handleCheckboxChange(test, e.target.checked)}
                                        >
                                            Aufzeichnung
                                        </Checkbox>
                                        <Button danger style={{marginRight: '8px', border: '0px'}}
                                                onClick={() => handleRemoveTest(test)}>
                                            <DeleteOutlined/>
                                        </Button>
                                    </Col>
                                </Row>
                            </List.Item>
                        )}
                    />
                </div>
            </Modal>
        </>
    );
};

export default TestPlanCreationModal;
